import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Loader2, Calendar, MapPin, User, AlertCircle, PartyPopper } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventCheckinPage() {
  const { token } = useParams();
  const [checkinInfo, setCheckinInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [checkinResult, setCheckinResult] = useState(null);

  useEffect(() => {
    const fetchCheckinInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/event-checkin/${token}`);
        if (response.ok) {
          const data = await response.json();
          setCheckinInfo(data);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Invalid check-in code');
        }
      } catch (err) {
        setError('Failed to load check-in information');
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchCheckinInfo();
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheckin = async () => {
    setProcessing(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/public/event-checkin/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCheckinResult(data);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Check-in failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const formatEventDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'EEEE, MMMM d, yyyy • h:mm a');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-gold mx-auto mb-4" />
          <p className="text-slate-300">Loading check-in...</p>
        </div>
      </div>
    );
  }

  if (error && !checkinInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl bg-white/95">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-3">
              Invalid Check-in Code
            </h2>
            <p className="text-slate-600">
              {error}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state after check-in
  if (checkinResult) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-900 to-emerald-800 flex items-center justify-center p-4" data-testid="checkin-success">
        <Card className="w-full max-w-md border-0 shadow-2xl bg-white/95">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-24 h-24 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              {checkinResult.already_attended ? (
                <CheckCircle2 className="w-12 h-12 text-emerald-600" />
              ) : (
                <PartyPopper className="w-12 h-12 text-emerald-600" />
              )}
            </div>
            <h2 className="text-3xl font-bold text-slate-800 mb-2">
              {checkinResult.already_attended ? 'Already Checked In' : 'Welcome!'}
            </h2>
            <p className="text-xl text-emerald-600 font-semibold mb-6">
              {checkinResult.lead_name}
            </p>
            
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-slate-800 mb-2">{checkinResult.event_name}</h3>
              <p className="text-sm text-slate-500">
                Checked in at {checkinResult.checked_in_at ? formatEventDate(checkinResult.checked_in_at) : 'Just now'}
              </p>
            </div>
            
            <div className="text-5xl mb-4">🎉</div>
            <p className="text-slate-600">
              {checkinResult.already_attended 
                ? "You've already checked in to this event."
                : "You're all set! Enjoy the event."
              }
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already attended state
  if (checkinInfo?.is_already_attended) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl bg-white/95">
          <CardContent className="pt-12 pb-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              Already Checked In
            </h2>
            <p className="text-xl text-emerald-600 font-semibold mb-6">
              {checkinInfo.lead_name}
            </p>
            
            <div className="bg-slate-50 rounded-xl p-6 text-left space-y-3">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-800">{checkinInfo.event_name}</p>
                  <p className="text-sm text-slate-500">{checkinInfo.event_category}</p>
                </div>
              </div>
              {checkinInfo.event_date && (
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <p className="text-sm text-slate-600">{formatEventDate(checkinInfo.event_date)}</p>
                </div>
              )}
              {checkinInfo.event_location && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-slate-400" />
                  <p className="text-sm text-slate-600">{checkinInfo.event_location}</p>
                </div>
              )}
            </div>
            
            <p className="text-slate-500 mt-6 text-sm">
              Checked in at {checkinInfo.checked_in_at ? formatEventDate(checkinInfo.checked_in_at) : 'Earlier'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pre-checkin state - show event info and check-in button
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4" data-testid="event-checkin-page">
      <Card className="w-full max-w-md border-0 shadow-2xl bg-white/95">
        <CardContent className="pt-8 pb-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 rounded-xl mb-4">
              <span className="text-gold font-heading text-2xl font-bold">G</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">Event Check-in</h1>
            <p className="text-slate-500">Tap below to confirm your attendance</p>
          </div>

          {/* Guest Info */}
          <div className="bg-gold/10 rounded-xl p-6 mb-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <User className="w-5 h-5 text-gold" />
              <span className="text-sm text-slate-500 uppercase tracking-wide">Guest</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{checkinInfo?.lead_name}</p>
          </div>

          {/* Event Details */}
          <div className="bg-slate-50 rounded-xl p-6 mb-6 space-y-4">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-gold mt-0.5" />
              <div>
                <p className="font-semibold text-slate-800">{checkinInfo?.event_name}</p>
                <p className="text-sm text-slate-500">{checkinInfo?.event_category}</p>
              </div>
            </div>
            {checkinInfo?.event_date && (
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-slate-400" />
                <p className="text-sm text-slate-600">{formatEventDate(checkinInfo.event_date)}</p>
              </div>
            )}
            {checkinInfo?.event_location && (
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-slate-400" />
                <p className="text-sm text-slate-600">{checkinInfo.event_location}</p>
              </div>
            )}
          </div>

          {/* Current Status */}
          <div className="text-center mb-6">
            <span className="text-sm text-slate-500">Current Status: </span>
            <span className="text-sm font-medium text-slate-800">{checkinInfo?.status}</span>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm mb-4 text-center">
              {error}
            </div>
          )}

          {/* Check-in Button */}
          <Button
            onClick={handleCheckin}
            disabled={processing}
            className="w-full h-14 text-lg bg-gold hover:bg-gold/90 text-slate-900 font-bold rounded-xl shadow-lg"
            data-testid="checkin-btn"
          >
            {processing ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Checking In...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5 mr-2" />
                Check In Now
              </>
            )}
          </Button>

          <p className="text-center text-xs text-slate-400 mt-6">
            Goldsands Group INC • Event Registration
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
